// Der Export der Kontaktsuche (ST-SEA-002 F2, Schnitt 3; ADR-0062): der Knopf
// mit Dialog und die Liste "Meine Exporte" der Seite /exporte.
//
// Die Liste hat eine eigene Seite, erreichbar ueber das Kontomenue und den
// Link in der Meldung nach dem Anlegen: Exporte entstehen in den Kontakten
// UND in Sammlungen, und eine Adresse kann jede Meldung verlinken (L13-01a).
// Sie fragt beim Aufbau und danach nur, solange ein Export laeuft (alle 3 s).
//
// "Suchen" bleibt die einzige primaere Schaltflaeche der Seite: "Exportieren"
// ist sekundaer, die primaere Aktion im Dialog gilt nur dort.
//
// DIE STATUSREGION "Status: Export" spricht nur Zustandswechsel (wird erstellt -> fertig oder fehlgeschlagen),
// das Speichern und das Verwerfen; nach 5 s ist sie wieder leer. Die Liste
// selbst ist nicht live.

import { useCallback, useEffect, useRef, useState, type ReactElement } from 'react';
import {
  Body1,
  Button,
  Caption1,
  Checkbox,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
  makeStyles,
  MessageBar,
  MessageBarActions,
  MessageBarBody,
  MessageBarTitle,
  ProgressBar,
  Radio,
  RadioGroup,
} from '@fluentui/react-components';

import type { PlainMessage } from '../access.ts';
import { newClientRequestId } from '../client-request.ts';
import {
  COLLECTION_EXPORT_GONE,
  collectionExportScopeSentence,
  CONFIRMATION_REQUIRED,
  createExport,
  downloadExport,
  EXPORT_COLUMN_LABELS,
  EXPORT_FORMAT_LABELS,
  EXPORT_ALL_HINT,
  EXPORT_COLLECTION_HINT,
  EXPORT_GONE,
  EXPORT_NOTICE,
  EXPORT_NOTICE_CHECKBOX,
  EXPORT_NOTICE_MISSING,
  exportStartedSentence,
  isExportOmitChannel,
  SUPPRESSED_FIELD_LABEL,
  SUPPRESSED_HINT,
  SUPPRESSED_MARK_LABEL,
  SUPPRESSED_OMIT_LABELS,
  type ExportOmitChannel,
  EXPORT_UNCLEAR,
  exportScopeSentence,
  fetchExports,
  isRunning,
  MARKING_EXPIRED,
  MARKING_FAILED,
  MARKING_PROGRESS_LABEL,
  MEINE_EXPORTE_HREF,
  MEINE_EXPORTE_LINK,
  needsMarking,
  revokeExport,
  statusSentence,
  timeLabel,
  uploadMarking,
  type ExportFormat,
  type ExportSummary,
  EXPORT_TOO_MANY_CONTEXTS,
  exportTooManyContexts,
} from '../exports.ts';
import { useEigeneHausarten } from '../use-eigene-hausarten.ts';
import type { SearchFilter } from '../parties.ts';
import { AppLink } from '../shell/router.tsx';
import { VorgangNach, Zustand } from '../shell/zustand.tsx';
import { NebenKnopf } from '../shell/nebenknopf.tsx';

const useStyles = makeStyles({
  liste: { listStyle: 'none', margin: '0.5rem 0 0', padding: 0, display: 'flex', flexDirection: 'column', gap: '0.5rem' },
  zeile: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.5rem', overflowWrap: 'anywhere' },
  hinweis: { overflowWrap: 'anywhere' },
  aktionen: { display: 'flex', flexWrap: 'wrap', gap: '0.5rem' },
  ziel: { minHeight: '24px', minWidth: '24px' },
  inhalt: { display: 'flex', flexDirection: 'column', gap: '0.75rem' },
  verborgen: { position: 'absolute', width: '1px', height: '1px', overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap' },
});

export const EXPORT_BUTTON_ID = 'kontakte-export-knopf';
/** Der Satz, was exportiert wird: beschreibt den Dialog, damit er beim Oeffnen mit angesagt wird. */
const EXPORT_SCOPE_ID = 'kontakte-export-umfang';
const EXPORT_CREATE_ID = 'kontakte-export-anlegen';
const EXPORT_CONFIRM_ID = 'kontakte-export-trotzdem';

/** "500 von 1.240" -- gezaehlt werden die gesendeten Kennungen, nicht sichtbare Kontakte. */
function transferText(gesendet: number, gesamt: number): string {
  return `${gesendet.toLocaleString('de-DE')} von ${gesamt.toLocaleString('de-DE')}`;
}
export const EXPORT_STATUS_REGION_ID = 'kontakte-export-status';
const NEU_IN_KONTAKTEN = 'In den Kontakten neu anlegen';
const POLL_MILLISECONDS = 3000;

const UNERREICHBAR: PlainMessage = Object.freeze({
  headline: 'LIAISON ist gerade nicht erreichbar.',
  explanation: 'Deine Exporte ließen sich nicht laden.',
  nextStep: 'Prüfe deine Verbindung und versuche es erneut.',
  retryable: true,
});
const SAVED_SENTENCE_MILLISECONDS = 5000;

export const exportRowId = (exportId: string): string => `kontakte-export-${exportId}`;

interface Zugang {
  readonly apiBaseUrl: string;
  readonly accessToken: () => Promise<string | undefined>;
  readonly onReauthenticate: () => void;
}

export interface MeineExporte {
  readonly items: readonly ExportSummary[];
  /** Erst nach der ersten Antwort ist "Noch keine Exporte" wahr. */
  readonly geladen: boolean;
  readonly fehler?: PlainMessage | undefined;
  readonly laden: () => Promise<void>;
  readonly ansage: string;
  readonly ansagen: (text: string) => void;
}

/** Die eigene Liste: einmal beim Aufbau, dann nur bei offenem Abschnitt und laufendem Export. */
export function useMeineExporte(zugang: Zugang, offen: boolean, aktiv: boolean): MeineExporte {
  const { apiBaseUrl, accessToken, onReauthenticate } = zugang;
  const [items, setItems] = useState<readonly ExportSummary[]>([]);
  const [geladen, setGeladen] = useState(false);
  const [fehler, setFehler] = useState<PlainMessage | undefined>(undefined);
  const [ansage, setAnsage] = useState('');
  const vorher = useRef<ReadonlyMap<string, ExportSummary['status']>>(new Map());
  const ansageZeit = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  // Ueberschneiden sich zwei Abrufe, gilt nur die Antwort des juengsten.
  const generation = useRef(0);

  const ansagen = useCallback((text: string) => {
    setAnsage(text);
    if (ansageZeit.current !== undefined) clearTimeout(ansageZeit.current);
    // In Ruhe traegt hoechstens eine Region Text: der Satz geht nach kurzer Zeit.
    ansageZeit.current = setTimeout(() => setAnsage(''), SAVED_SENTENCE_MILLISECONDS);
  }, []);

  const laden = useCallback(async () => {
    const meine = ++generation.current;
    const token = await accessToken();
    if (token === undefined) {
      onReauthenticate();
      return;
    }
    const result = await fetchExports({ baseUrl: apiBaseUrl, accessToken: token }).catch(() => undefined);
    if (meine !== generation.current) {
      return;
    }
    // Ein Fehler beim Abfragen laesst die vorhandene Liste stehen.
    if (result === undefined) {
      setFehler(UNERREICHBAR);
      return;
    }
    if (result.outcome === 'rejected') {
      setFehler(result.message);
      return;
    }
    setGeladen(true);
    setFehler(undefined);
    // Nur Zustandswechsel werden angesagt, nie jede Abfrage.
    for (const item of result.exports) {
      const alt = vorher.current.get(item.exportId);
      if ((alt === 'queued' || alt === 'running') && !isRunning(item)) {
        ansagen(`Export von ${timeLabel(item.requestedAt)}: ${statusSentence(item)}`);
      }
    }
    vorher.current = new Map(result.exports.map((item) => [item.exportId, item.status]));
    setItems(result.exports);
  }, [accessToken, apiBaseUrl, onReauthenticate, ansagen]);

  useEffect(() => {
    if (aktiv) void laden();
  }, [aktiv, laden]);

  const laeuft = items.some(isRunning);
  useEffect(() => {
    if (!aktiv || !offen || !laeuft) return undefined;
    const timer = setInterval(() => void laden(), POLL_MILLISECONDS);
    return () => clearInterval(timer);
  }, [aktiv, offen, laeuft, laden]);

  useEffect(() => () => {
    if (ansageZeit.current !== undefined) clearTimeout(ansageZeit.current);
  }, []);

  return { items, geladen, fehler, laden, ansage, ansagen };
}

export type ExportMeldungArt = 'angelegt' | 'unklar';
export type { ExportOmitChannel };

/**
 * Die eine Ansage nach dem Anlegen (L13-01a), in Kontaktliste und Sammlung
 * gleich: Fluents MessageBar sagt sich selbst an, daneben spricht keine
 * Statusregion. Sie bleibt stehen, bis man sie schliesst. Jedes neue Anlegen
 * haengt sie per `key` neu ein, damit sie sich wieder ansagt.
 */
export function ExportMeldung(props: {
  readonly art: ExportMeldungArt;
  /** Der Kanal, fuer den Gesperrte weggelassen werden; ohne Angabe markiert. */
  readonly weggelassen?: ExportOmitChannel | undefined;
  readonly onSchliessen: () => void;
}): ReactElement {
  return (
    <MessageBar intent={props.art === 'angelegt' ? 'info' : 'warning'} politeness="polite">
      <MessageBarBody>
        {props.art === 'angelegt' ? exportStartedSentence(props.weggelassen) : EXPORT_UNCLEAR} <AppLink to={MEINE_EXPORTE_HREF}>{MEINE_EXPORTE_LINK}</AppLink>
      </MessageBarBody>
      <MessageBarActions>
        <NebenKnopf
          size="small"
          onClick={() => {
            // Der Knopf haengt sich selbst aus: den Fokus vorher an "Exportieren"
            // geben, sonst fiele er auf BODY (Codex-Cloud P2 an #542).
            (document.getElementById(EXPORT_BUTTON_ID) ?? document.getElementById('hauptbereich'))?.focus();
            props.onSchliessen();
          }}
        >
          Schließen
        </NebenKnopf>
      </MessageBarActions>
    </MessageBar>
  );
}

function Meldung(props: { readonly message: PlainMessage; readonly alert?: boolean }): ReactElement {
  return (
    <>
      <MessageBar intent="warning" {...(props.alert === true ? { role: 'alert' } : {})}>
        <MessageBarBody>
          <MessageBarTitle>{props.message.headline}</MessageBarTitle>
          <div>{props.message.explanation}</div>
          <div>{props.message.nextStep}</div>
        </MessageBarBody>
      </MessageBar>
      {/* Hinter dem Alert, nicht in ihm: die Zeile hat ihre eigene Ansage (#534). */}
      <VorgangNach meldungen={[props.message]} />
    </>
  );
}

/**
 * Woraus exportiert wird: eine Suche (Kontaktliste), eine ganze Sammlung
 * (ADR-0065 Nachtrag 4a) oder die Auswahl der Liste ohne Suche. Der Server
 * nimmt die Sammlung nur allein, ohne Begriff, Filter und Kennungen, und
 * Kennungen nur mit Suchgrund; die Auswahl ohne Suche reist deshalb immer als
 * Markierung (Nachtrag 4b), mit derselben Sperrregel und demselben Audit.
 */
export type ExportQuelle =
  | {
      readonly art: 'suche';
      readonly suche: { readonly term?: string | undefined; readonly filter?: SearchFilter | undefined };
      /** Die Trefferzahl in Worten, leer, wenn sie fehlt. */
      readonly zahl: string;
    }
  | { readonly art: 'sammlung'; readonly collectionId: string; readonly name: string; readonly anzahl: number }
  | { readonly art: 'auswahl' };

/** Knopf "Exportieren" und der Dialog: Hinweis mit Pflicht-Haekchen, ueber 5.000 ein zweiter Schritt. */
export function ExportKnopf(
  props: Zugang & {
    readonly quelle: ExportQuelle;
    /** Die markierten Zeilen; leer heisst: die ganze Quelle. */
    readonly markiert: readonly string[];
    /** primary nur, wo der Export die Hauptaktion der Seite ist (Sammlung). */
    readonly appearance?: 'primary' | 'secondary';
    /** Die Sammlung gibt es nicht mehr (404): die Ansicht zeigt das nach dem Schliessen. */
    readonly onQuelleWeg?: () => void;
    readonly onAngelegt: (item: ExportSummary, weggelassen: ExportOmitChannel | undefined) => void;
    /** Der Ausgang ist offen (Antwort abgebrochen): die Liste neu laden. */
    readonly onUnklar: () => void;
    readonly offen: boolean;
    readonly onOffen: (offen: boolean) => void;
  },
): ReactElement {
  const own = useStyles();
  // Fuer "so viele Verlage …" nach der Art der eigenen Haeuser (M7).
  const eigeneArten = useEigeneHausarten(props.offen, props.apiBaseUrl, props.accessToken);
  const angezeigt = (meldung: PlainMessage): PlainMessage => (meldung === EXPORT_TOO_MANY_CONTEXTS ? exportTooManyContexts(eigeneArten.arten) : meldung);
  // Haengt die Meldung an den Arten, wartet sie, bis sie geklaert sind (hoechstens 1,5 s).
  const zeigbar = (meldung: PlainMessage | undefined): meldung is PlainMessage => meldung !== undefined && (meldung !== EXPORT_TOO_MANY_CONTEXTS || eigeneArten.geklaert);
  const [bestaetigt, setBestaetigt] = useState(false);
  const [format, setFormat] = useState<ExportFormat>('xlsx');
  const [gesperrte, setGesperrte] = useState<'markieren' | ExportOmitChannel>('markieren');
  const weggelassen = gesperrte === 'markieren' ? undefined : gesperrte;
  const [haekchenFehler, setHaekchenFehler] = useState(false);
  const [schritt, setSchritt] = useState<'eingabe' | 'gross'>('eingabe');
  const [fehler, setFehler] = useState<PlainMessage | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const anfrage = useRef<string>(newClientRequestId());
  const grossTitel = useRef<HTMLHeadingElement>(null);
  // Ueber 250 Markierte (ADR-0065 Nachtrag 4b): die hochgeladene Markierung lebt
  // im Dialog und dient auch dem zweiten Schritt ueber 5.000; beim Schliessen
  // wird sie vergessen. Abbrechen stoppt weitere Bloecke; die halbe Markierung
  // beendet der Fegelauf nach 60 min, die Oberflaeche sagt dazu nichts.
  const markierung = useRef<string | undefined>(undefined);
  // Jeder Versuch hat eine Nummer; Abbrechen macht den laufenden ungueltig. So
  // stoppt er vor dem naechsten Block, und ein neuer Versuch nach erneutem
  // Oeffnen geht sofort, ohne auf den alten zu warten.
  const lauf = useRef(0);
  const aktiverLauf = useRef<number | undefined>(undefined);
  const [fortschritt, setFortschritt] = useState<number | undefined>(undefined);

  const schliessen = (): void => {
    // Den Fokus vor dem Aushaengen abgeben, sonst bleiben Fluents Fokuswaechter
    // im Body stehen (axe aria-hidden-focus); das Ziel setzt der Aufrufer danach.
    (document.activeElement as HTMLElement | null)?.blur();
    // Der Hinweis wird bei JEDEM neuen Oeffnen frisch bestaetigt.
    setBestaetigt(false);
    // Ebenso die Wahl zu Gesperrten: jedes Oeffnen beginnt bei "Markieren".
    setGesperrte('markieren');
    markierung.current = undefined;
    setFortschritt(undefined);
    props.onOffen(false);
    setSchritt('eingabe');
    setFehler(undefined);
    setHaekchenFehler(false);
  };

  /** Abbrechen und Esc: auch waehrend der Uebertragung; der Fokus geht an "Exportieren" zurueck. */
  const abbrechen = (): void => {
    lauf.current += 1;
    setBusy(false);
    setFortschritt(undefined);
    schliessen();
    setTimeout(() => document.getElementById(EXPORT_BUTTON_ID)?.focus(), 0);
  };

  useEffect(() => {
    if (schritt === 'gross') grossTitel.current?.focus();
  }, [schritt]);

  const ueberMarkierung = props.quelle.art === 'auswahl' || needsMarking(props.markiert.length);

  const anlegen = async (gross: boolean): Promise<void> => {
    // Ein zweites Absenden desselben Versuchs zaehlt nicht.
    if (aktiverLauf.current === lauf.current) return;
    if (!bestaetigt) {
      setHaekchenFehler(true);
      document.getElementById('kontakte-export-hinweis')?.focus();
      return;
    }
    const meinLauf = lauf.current;
    const abgebrochen = (): boolean => lauf.current !== meinLauf;
    aktiverLauf.current = meinLauf;
    setBusy(true);
    setFehler(undefined);
    const misslungen = (message: PlainMessage): void => {
      setFehler(message);
      // Der Fokus bleibt auf dem Knopf, mit dem es weitergeht.
      setTimeout(() => document.getElementById(gross ? EXPORT_CONFIRM_ID : EXPORT_CREATE_ID)?.focus(), 0);
    };
    try {
      const token = await props.accessToken();
      if (token === undefined) {
        props.onReauthenticate();
        return;
      }
      if (ueberMarkierung && markierung.current === undefined) {
        // Ein neuer Versuch legt eine NEUE Markierung an. Die Leiste erst ab dem
        // zweiten Block: bei einem gibt es keinen Zwischenstand, sie blitzte nur auf
        // (DESIGN); dann sagt der Knopf "Wird angelegt …".
        const bloecke = needsMarking(props.markiert.length);
        if (bloecke) setFortschritt(0);
        const hochgeladen = await uploadMarking({
          baseUrl: props.apiBaseUrl,
          accessToken: token,
          partyIds: props.markiert,
          clientRequestId: newClientRequestId(),
          abgebrochen,
          fortschritt: (gesendet) => {
            if (bloecke) setFortschritt(gesendet);
          },
        });
        if (abgebrochen() || hochgeladen.outcome === 'abgebrochen') return;
        setFortschritt(undefined);
        if (hochgeladen.outcome !== 'hochgeladen') {
          misslungen(hochgeladen.outcome === 'abgelehnt' ? hochgeladen.message : MARKING_FAILED);
          return;
        }
        markierung.current = hochgeladen.markingId;
      }
      const ganzeSammlung = props.quelle.art === 'sammlung' && props.markiert.length === 0;
      const result = await createExport({
        baseUrl: props.apiBaseUrl,
        accessToken: token,
        ...(markierung.current !== undefined
          ? { markingId: markierung.current }
          : props.quelle.art === 'sammlung'
            ? ganzeSammlung
              ? { collectionId: props.quelle.collectionId }
              : { partyIds: props.markiert }
            : props.quelle.art === 'suche'
              ? { ...props.quelle.suche, partyIds: props.markiert }
              : { partyIds: props.markiert }),
        format,
        confirmedLargeExport: gross,
        omitSuppressedChannel: weggelassen,
        clientRequestId: anfrage.current,
      });
      if (abgebrochen()) {
        // Abgebrochen, waehrend der Export schon angelegt wurde: nicht verschlucken,
        // "Meine Exporte" laedt neu und zeigt ihn.
        if (result.outcome === 'applied') props.onUnklar();
        return;
      }
      if (result.outcome === 'not-found' && ganzeSammlung) {
        anfrage.current = newClientRequestId();
        props.onQuelleWeg?.();
        misslungen(COLLECTION_EXPORT_GONE);
        return;
      }
      if (result.outcome === 'not-found' && markierung.current !== undefined) {
        markierung.current = undefined;
        anfrage.current = newClientRequestId();
        misslungen(MARKING_EXPIRED);
        return;
      }
      if (result.outcome === 'applied' && result.export !== undefined) {
        anfrage.current = newClientRequestId();
        schliessen();
        props.onAngelegt(result.export, weggelassen);
        return;
      }
      if (result.outcome === 'conflict' && result.message === CONFIRMATION_REQUIRED) {
        // Eine neue Anfrage, nicht die Wiederholung der alten.
        anfrage.current = newClientRequestId();
        setSchritt('gross');
        return;
      }
      if (result.outcome === 'invalid') {
        setHaekchenFehler(result.errors.some((error) => error.fieldPath === 'noticeAcknowledged'));
        setFehler(
          props.quelle.art === 'suche'
            ? { headline: 'Der Export ließ sich nicht anlegen.', explanation: 'LIAISON hat die Suche für den Export nicht angenommen.', nextStep: 'Schließe den Dialog, suche noch einmal und exportiere dann.', retryable: false }
            : { headline: 'Der Export ließ sich nicht anlegen.', explanation: 'LIAISON hat den Auftrag nicht angenommen.', nextStep: props.quelle.art === 'sammlung' ? 'Schließe den Dialog, lade die Sammlung neu und exportiere dann.' : 'Schließe den Dialog, lade die Liste neu und exportiere dann.', retryable: false },
        );
        return;
      }
      setFehler(result.outcome === 'applied' ? EXPORT_GONE : result.message);
      if (!(result.outcome === 'rejected' && result.message.retryable)) {
        anfrage.current = newClientRequestId();
      }
    } catch {
      if (abgebrochen()) {
        // Der Ausgang ist offen; die Liste zeigt, ob etwas entstand.
        props.onUnklar();
        return;
      }
      setFortschritt(undefined);
      // Ein Netzfehler beim Hochladen: nichts angelegt, die naechste Markierung ist neu.
      if (ueberMarkierung && markierung.current === undefined) {
        misslungen(MARKING_FAILED);
        return;
      }
      // Ein Netzfehler: dieselbe id fuer den naechsten Versuch (ADR-0060).
      setFehler({ headline: 'LIAISON ist gerade nicht erreichbar.', explanation: 'Ob der Export angelegt wurde, zeigt „Meine Exporte“.', nextStep: 'Prüfe deine Verbindung und versuch es erneut.', retryable: true });
      props.onUnklar();
    } finally {
      if (!abgebrochen()) {
        aktiverLauf.current = undefined;
        setBusy(false);
      }
    }
  };

  // In beiden Schritten: auch "Trotzdem exportieren" laedt neu hoch, wenn die Markierung inzwischen abgelaufen ist.
  const fortschrittsAnzeige = (
    <>
      {/* Einmal angesagt beim Start; den Zwischenstand traegt die Leiste. */}
      <div role="status" aria-live="polite" aria-label="Status: Übertragen" className={own.verborgen}>
        {fortschritt !== undefined ? `${MARKING_PROGRESS_LABEL}.` : ''}
      </div>
      {fortschritt !== undefined ? (
        <Field label={MARKING_PROGRESS_LABEL} hint={transferText(fortschritt, props.markiert.length)}>
          <ProgressBar value={fortschritt} max={props.markiert.length} aria-valuetext={transferText(fortschritt, props.markiert.length)} />
        </Field>
      ) : undefined}
    </>
  );

  const knopfText = (bereit: string): string => (fortschritt !== undefined ? 'Wird vorbereitet …' : busy ? 'Wird angelegt …' : bereit);

  return (
    <>
      <Button id={EXPORT_BUTTON_ID} type="button" appearance={props.appearance ?? 'secondary'} onClick={() => props.onOffen(true)}>
        Exportieren
      </Button>
      {/* Nur eingehaengt, wenn er gebraucht wird: Fluent setzt sonst Fokuswaechter
          in den Body, die axe als aria-hidden-focus meldet (wie stichworte.tsx). */}
      {props.offen ? (
        <Dialog open onOpenChange={(_, data) => { if (!data.open) abbrechen(); }}>
          <DialogSurface {...(schritt === 'eingabe' ? { 'aria-describedby': EXPORT_SCOPE_ID } : {})}>
            <DialogBody>
              {schritt === 'eingabe' ? (
                <>
                  <DialogTitle>Kontakte exportieren</DialogTitle>
                  {/* Flex-Spalte: Body1 und Caption1 sind inline und klebten sonst in einer Zeile. */}
                  <DialogContent className={own.inhalt}>
                    <Body1 id={EXPORT_SCOPE_ID}>
                      {props.quelle.art === 'sammlung' && props.markiert.length === 0
                        ? collectionExportScopeSentence(props.quelle.name, props.quelle.anzahl)
                        : exportScopeSentence(props.markiert.length, props.quelle.art === 'suche' ? props.quelle.zahl : '')}
                    </Body1>
                    {/* Eine Zeile statt Überschrift und Liste: die Namen sind die Spaltenköpfe der Datei, in ihrer Reihenfolge. */}
                    <Body1>{`Spalten in der Datei: ${EXPORT_COLUMN_LABELS.join(', ')}.`}</Body1>
                    {fortschrittsAnzeige}
                    {/* Ohne Suche gibt es kein "alle" ausser der Auswahl. */}
                    {props.markiert.length > 0 && props.quelle.art !== 'auswahl' ? <Caption1>{props.quelle.art === 'suche' ? EXPORT_ALL_HINT : EXPORT_COLLECTION_HINT}</Caption1> : undefined}
                    <Field label="Format">
                      <RadioGroup value={format} onChange={(_e, data) => setFormat(data.value === 'csv' ? 'csv' : 'xlsx')}>
                        <Radio value="xlsx" label={EXPORT_FORMAT_LABELS.xlsx} />
                        <Radio value="csv" label={EXPORT_FORMAT_LABELS.csv} />
                      </RadioGroup>
                    </Field>
                    <Field label={SUPPRESSED_FIELD_LABEL} hint={SUPPRESSED_HINT}>
                      <RadioGroup value={gesperrte} onChange={(_e, data) => setGesperrte(isExportOmitChannel(data.value) ? data.value : 'markieren')}>
                        <Radio value="markieren" label={SUPPRESSED_MARK_LABEL} />
                        <Radio value="email" label={SUPPRESSED_OMIT_LABELS.email} />
                        <Radio value="phone" label={SUPPRESSED_OMIT_LABELS.phone} />
                        <Radio value="postal" label={SUPPRESSED_OMIT_LABELS.postal} />
                      </RadioGroup>
                    </Field>
                    <Body1 className={own.hinweis}>
                      <strong>{EXPORT_NOTICE}</strong>
                    </Body1>
                    <Field
                      validationMessage={haekchenFehler ? EXPORT_NOTICE_MISSING : null}
                      validationState={haekchenFehler ? 'error' : 'none'}
                    >
                      <Checkbox
                        id="kontakte-export-hinweis"
                        label={EXPORT_NOTICE_CHECKBOX}
                        checked={bestaetigt}
                        onChange={(_e, data) => {
                          setBestaetigt(data.checked === true);
                          if (data.checked === true) setHaekchenFehler(false);
                        }}
                      />
                    </Field>
                    {zeigbar(fehler) ? <Meldung message={angezeigt(fehler)} alert /> : undefined}
                  </DialogContent>
                  <DialogActions className={own.aktionen}>
                    <NebenKnopf onClick={abbrechen}>
                      Abbrechen
                    </NebenKnopf>
                    {/* disabledFocusable statt disabled: der Fokus bleibt im Dialog, Esc erreicht ihn auch waehrend der Uebertragung. */}
                    <Button id={EXPORT_CREATE_ID} appearance="primary" disabledFocusable={busy} onClick={() => void anlegen(false)}>
                      {knopfText('Export anlegen')}
                    </Button>
                  </DialogActions>
                </>
              ) : (
                <>
                  <DialogTitle as="h2">
                    <span ref={grossTitel} tabIndex={-1}>
                      {CONFIRMATION_REQUIRED.headline}
                    </span>
                  </DialogTitle>
                  <DialogContent>
                    <Body1>
                      {/* "Grenze die Suche ein" passt nur zur Suche. */}
                      {props.quelle.art === 'suche' ? `${CONFIRMATION_REQUIRED.explanation} ${CONFIRMATION_REQUIRED.nextStep}` : CONFIRMATION_REQUIRED.explanation}
                    </Body1>
                    {fortschrittsAnzeige}
                    {zeigbar(fehler) ? <Meldung message={angezeigt(fehler)} alert /> : undefined}
                  </DialogContent>
                  <DialogActions className={own.aktionen}>
                    <NebenKnopf onClick={abbrechen}>
                      Abbrechen
                    </NebenKnopf>
                    {/* Eine Sammlung hat keine Suche, die man eingrenzen koennte. */}
                    {props.quelle.art === 'suche' ? (
                      <Button
                        appearance="secondary"
                        onClick={() => {
                          schliessen();
                          // Nach dem Schliessen gibt Fluent den Fokus an den Ausloeser zurueck;
                          // erst danach ins Suchfeld.
                          setTimeout(() => document.getElementById('kontakte-suchbegriff')?.focus(), 0);
                        }}
                      >
                        Suche eingrenzen
                      </Button>
                    ) : undefined}
                    <Button id={EXPORT_CONFIRM_ID} appearance="primary" disabledFocusable={busy} onClick={() => void anlegen(true)}>
                      {knopfText('Trotzdem exportieren')}
                    </Button>
                  </DialogActions>
                </>
              )}
            </DialogBody>
          </DialogSurface>
        </Dialog>
      ) : undefined}
    </>
  );
}

/** Der Abschnitt "Meine Exporte": zugeklappt, mit Herunterladen, Verwerfen und Neu anlegen. */
export function MeineExporteListe(
  props: Zugang & {
    readonly exporte: MeineExporte;
  },
): ReactElement {
  const own = useStyles();
  const { exporte } = props;
  const [laedt, setLaedt] = useState<string | undefined>(undefined);
  const [verwerfen, setVerwerfen] = useState<string | undefined>(undefined);
  const [verwirft, setVerwirft] = useState(false);
  // Fokus nach dem Wechsel zwischen "Verwerfen" und seiner Bestaetigung.
  const fokusNach = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (fokusNach.current === undefined) return;
    const ziel = document.getElementById(fokusNach.current);
    if (ziel !== null) {
      fokusNach.current = undefined;
      ziel.focus();
    }
  });
  const [zeilenFehler, setZeilenFehler] = useState<{ readonly exportId: string; readonly message: PlainMessage } | undefined>(undefined);

  const mitToken = async <T,>(run: (token: string) => Promise<T>): Promise<T | undefined> => {
    const token = await props.accessToken();
    if (token === undefined) {
      props.onReauthenticate();
      return undefined;
    }
    return run(token);
  };

  const herunterladen = async (item: ExportSummary): Promise<void> => {
    setLaedt(item.exportId);
    setZeilenFehler(undefined);
    try {
      const result = await mitToken((token) => downloadExport({ baseUrl: props.apiBaseUrl, accessToken: token, exportId: item.exportId }));
      if (result === undefined) return;
      if (result.outcome !== 'file') {
        setZeilenFehler({ exportId: item.exportId, message: result.outcome === 'gone' ? EXPORT_GONE : result.message });
        void exporte.laden();
        return;
      }
      const url = URL.createObjectURL(result.blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = result.fileName;
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 0);
      exporte.ansagen(`Datei gespeichert: ${result.fileName}`);
    } catch {
      setZeilenFehler({ exportId: item.exportId, message: { headline: 'Die Datei ließ sich nicht laden.', explanation: 'Die Verbindung brach ab oder der Speicher reichte nicht.', nextStep: 'Versuch es erneut.', retryable: true } });
    } finally {
      setLaedt(undefined);
    }
  };

  const verwerfenBestaetigt = async (item: ExportSummary, index: number): Promise<void> => {
    if (verwirft) return;
    setVerwirft(true);
    // Das Ziel des Fokus aus der Liste VOR dem Neuladen: die verworfene Zeile bleibt, die naechste auch.
    const naechste = exporte.items[index + 1] ?? exporte.items[index - 1];
    const result = await mitToken((token) =>
      revokeExport({ baseUrl: props.apiBaseUrl, accessToken: token, exportId: item.exportId, lockVersion: item.lockVersion }),
    ).catch(() => undefined);
    setVerwerfen(undefined);
    // Ein Konflikt (schon beendet, inzwischen geaendert) ist still: die neu
    // geladene Liste zeigt den Endzustand. Nur eine Ablehnung bekommt einen Satz.
    if (result !== undefined && result.outcome === 'rejected') {
      setZeilenFehler({ exportId: item.exportId, message: result.message });
    }
    await exporte.laden();
    setVerwirft(false);
    if (result !== undefined && result.outcome === 'applied') {
      exporte.ansagen(`Export von ${timeLabel(item.requestedAt)} verworfen.`);
    }
    // Der Fokus geht auf die naechste Zeile, sonst auf die eigene: sie bleibt als "Verworfen" stehen.
    fokusNach.current = exportRowId((naechste ?? item).exportId);
  };

  return (
    <div>
      <div role="status" aria-live="polite" aria-label="Status: Export" id={EXPORT_STATUS_REGION_ID} className={own.hinweis}>
        {exporte.ansage}
      </div>
      {exporte.fehler !== undefined ? (
        // Ein Lesefehler: nichts ist geschrieben, die Leiste ist ein Fehler. Die vorhandene Liste bleibt stehen.
        <MessageBar intent="error" className={own.hinweis}>
          <MessageBarBody>
            <MessageBarTitle>{exporte.fehler.headline}</MessageBarTitle>
            <div>{exporte.fehler.explanation}</div>
            <div>{exporte.fehler.nextStep}</div>
          </MessageBarBody>
          <MessageBarActions>
            <Button type="button" size="small" appearance="secondary" onClick={() => void exporte.laden()}>
              Erneut laden
            </Button>
          </MessageBarActions>
        </MessageBar>
      ) : undefined}
      {!exporte.geladen && exporte.fehler === undefined ? (
        <Body1>Wird geladen …</Body1>
      ) : exporte.geladen && exporte.items.length === 0 ? (
        <Zustand art="leer" eingebettet titel="Du hast noch keinen Export angelegt." saetze={[]} haupt={{ label: NEU_IN_KONTAKTEN, href: '/kontakte' }} />
      ) : exporte.items.length === 0 ? undefined : (
        <ul className={own.liste} aria-label="Meine Exporte">
          {exporte.items.map((item, index) => {
            const wann = timeLabel(item.requestedAt);
            // Mit Sekunden: zwei Exporte derselben Minute brauchen verschiedene Namen.
            const name = timeLabel(item.requestedAt, new Date(), true);
            return (
              <li key={item.exportId} id={exportRowId(item.exportId)} tabIndex={-1} className={own.zeile}>
                <Body1>
                  Export von {wann}: {statusSentence(item)}
                </Body1>
                {item.status === 'succeeded' ? (
                  <Button
                    type="button"
                    size="small"
                    appearance="secondary"
                    className={own.ziel}
                    disabledFocusable={laedt === item.exportId}
                    aria-label={laedt === item.exportId ? `Wird geladen, Export von ${name}` : `Herunterladen, Export von ${name}`}
                    onClick={() => void herunterladen(item)}
                  >
                    {laedt === item.exportId ? 'Wird geladen …' : 'Herunterladen'}
                  </Button>
                ) : undefined}
                {item.status === 'failed' ? (
                  // Die Suche des Exports lebt in den Kontakten; dort entsteht der neue.
                      // Alle diese Links fuehren an dasselbe Ziel, derselbe Name ist richtig.
                  <AppLink to="/kontakte" className={own.ziel}>
                    {NEU_IN_KONTAKTEN}
                  </AppLink>
                ) : undefined}
                {(isRunning(item) || item.status === 'succeeded') && verwerfen !== item.exportId ? (
                  <NebenKnopf
                    id={`kontakte-export-verwerfen-${item.exportId}`}
                    type="button"
                    size="small"
                    className={own.ziel}
                    aria-label={`Verwerfen, Export von ${name}`}
                    onClick={() => {
                      fokusNach.current = `kontakte-export-behalten-${item.exportId}`;
                      setVerwerfen(item.exportId);
                    }}
                  >
                    Verwerfen
                  </NebenKnopf>
                ) : undefined}
                {verwerfen === item.exportId ? (
                  <span className={own.zeile}>
                    <Caption1>Export von {wann} verwerfen? Die Datei wird gelöscht.</Caption1>
                    <NebenKnopf type="button" size="small" className={own.ziel} disabledFocusable={verwirft} onClick={() => void verwerfenBestaetigt(item, index)}>
                      Verwerfen
                    </NebenKnopf>
                    <NebenKnopf
                      id={`kontakte-export-behalten-${item.exportId}`}
                      type="button"
                      size="small"
                      className={own.ziel}
                      onClick={() => {
                        fokusNach.current = `kontakte-export-verwerfen-${item.exportId}`;
                        setVerwerfen(undefined);
                      }}
                    >
                      Behalten
                    </NebenKnopf>
                  </span>
                ) : undefined}
                {zeilenFehler?.exportId === item.exportId ? <Meldung message={zeilenFehler.message} /> : undefined}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
